import { computed } from 'vue'
import { useQuery, keepPreviousData } from '@tanstack/vue-query'
import { rouletteApi } from '@/api'
import type { SpinRead } from '@/api'
import { POLLING_INTERVALS, STALE_TIME, RETRY_CONFIG } from '@/config/query'
import { normalizeTonAddress } from '@/utils/ton-address'

// UI type for winner display
export interface Winner {
  address: string
  wallet: string // truncated
  amount: number
  timestamp: number
  tx?: string
}

interface UseWinningSpinsOptions {
  limit?: number
}

export function transformWinningSpins(spins: SpinRead[]): Winner[] {
  return spins.map((spin) => {
    const prize = spin.prizes[0]
    const address = normalizeTonAddress(spin.user.wallet)
    return {
      address,
      wallet: `${address.slice(0, 6)}...${address.slice(-4)}`,
      amount: prize?.amount ?? 0,
      timestamp: new Date(spin.created_at).getTime(),
      tx: prize?.explorer_link ?? undefined,
    }
  })
}

export function useWinningSpins(options: UseWinningSpinsOptions = {}) {
  const { limit = 10 } = options

  const { data, error, isFetching, refetch } = useQuery({
    queryKey: ['roulette', 'winners', limit],
    queryFn: async () => {
      const response = await rouletteApi.getWinningSpins(limit, 0)
      if (response.success && response.data) {
        return response.data
      }
      throw new Error(response.error ?? 'Failed to fetch winning spins')
    },
    refetchInterval: POLLING_INTERVALS.BACKEND_API,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    placeholderData: keepPreviousData,
    retry: RETRY_CONFIG.ATTEMPTS,
    staleTime: STALE_TIME.MEDIUM,
  })

  return {
    winningSpins: computed(() => {
      if (!data.value) return []
      return transformWinningSpins(data.value.items)
    }),
    isLoading: computed(() => isFetching.value && !data.value),
    hasError: computed(() => Boolean(error.value)),
    errorMessage: computed(() => error.value?.message ?? 'Failed to load winning spins'),
    refetch,
  }
}
